/**
 * Auto-list: typing "1. " (any 1-2 digit number) or "- " / "* " at the start
 * of a plain line turns it into a real list; numbering starts where the user
 * started (ol start). Built by hand — the execCommand list commands are dead
 * letters in modern Chrome (silent no-ops), while insertParagraph still works
 * and natively continues <li> numbering / exits on an empty item, so
 * Shift+Enter needs no code here.
 *
 * Caret discipline: restoring a caret into an EMPTY text node is unreliable
 * (Chrome normalizes it to the previous position and typed text lands on the
 * wrong line) — an empty item becomes <li><br></li> with the caret at (li, 0).
 */
export function maybeAutolist(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return false;
  const node = sel.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE) return false;
  const textNode = node as Text;
  if (!editor.contains(textNode)) return false;
  if (textNode.parentElement?.closest("li,ol,ul")) return false;

  const upto = (textNode.textContent ?? "").slice(0, sel.anchorOffset);
  const m = upto.match(/^(\d{1,2})\.\s$/) ?? upto.match(/^([-*])\s$/);
  if (!m) return false;

  // The line's block: the div/p holding the node, or null when the first line
  // is still a bare text node directly under the editor root.
  let block: HTMLElement | null =
    textNode.parentElement === editor ? null : textNode.parentElement;
  while (block && block.parentElement !== editor) block = block.parentElement;

  const rest = (textNode.textContent ?? "").slice(m[0].length);
  textNode.textContent = rest;
  const ordered = /\d/.test(m[1]);
  const list = document.createElement(ordered ? "ol" : "ul");
  if (ordered && m[1] !== "1") list.setAttribute("start", m[1]);
  const li = document.createElement("li");
  if (block) {
    block.parentNode?.replaceChild(list, block);
    while (block.firstChild) li.appendChild(block.firstChild);
  } else {
    editor.replaceChild(list, textNode);
    li.appendChild(textNode);
  }
  list.appendChild(li);
  if (!li.textContent) li.innerHTML = "<br>";

  const r = document.createRange();
  if (rest && li.firstChild && li.firstChild.nodeType === Node.TEXT_NODE)
    r.setStart(li.firstChild, 0);
  else r.setStart(li, 0);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  return true;
}
